import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Ship, Anchor, Globe, AlertTriangle, Clock, TrendingUp, Package,
    MapPin, Navigation, BarChart3, Filter, Search, RefreshCw, ChevronRight, ChevronLeft,
    ArrowRight, Activity, Zap, Eye, Bell, Loader2, Sparkles, X,
    CheckCircle, AlertCircle, Info, Truck, Calendar, ArrowUpRight, DollarSign,
    CircleDot, RotateCcw, FileWarning, ShieldAlert, Container, Warehouse, BoxSelect,
    ArrowDown, Boxes, Timer, Building2
} from 'lucide-react';
import axios from 'axios';
import LiveVesselMap from '../components/dashboard/LiveVesselMap';
import { baseURL } from '@/config/APIConfig';

const BACKEND_URL = baseURL.live || '';

const PORT_COORDS = {
    'USNYC': { x: 28, y: 35, name: 'New York', country: 'USA' },
    'USLAX': { x: 12, y: 40, name: 'Los Angeles', country: 'USA' },
    'USORF': { x: 27, y: 38, name: 'Norfolk', country: 'USA' },
    'USSAV': { x: 26, y: 42, name: 'Savannah', country: 'USA' },
    'USMIA': { x: 25, y: 46, name: 'Miami', country: 'USA' },
    'USHOU': { x: 20, y: 44, name: 'Houston', country: 'USA' },
    'NLRTM': { x: 48, y: 28, name: 'Rotterdam', country: 'Netherlands' },
    'GBFXT': { x: 46, y: 27, name: 'Felixstowe', country: 'UK' },
    'DEHAM': { x: 49, y: 26, name: 'Hamburg', country: 'Germany' },
    'CNSHA': { x: 80, y: 40, name: 'Shanghai', country: 'China' },
    'CNYTN': { x: 79, y: 44, name: 'Yantian', country: 'China' },
    'CNNGB': { x: 80, y: 41, name: 'Ningbo', country: 'China' },
    'HKHKG': { x: 78, y: 45, name: 'Hong Kong', country: 'China' },
    'SGSIN': { x: 75, y: 55, name: 'Singapore', country: 'Singapore' },
    'KRPUS': { x: 82, y: 37, name: 'Busan', country: 'South Korea' },
    'JPYOK': { x: 85, y: 36, name: 'Yokohama', country: 'Japan' },
    'AEDXB': { x: 60, y: 44, name: 'Dubai', country: 'UAE' },
    'INMUN': { x: 66, y: 48, name: 'Mumbai', country: 'India' },
    'BDDAC': { x: 71, y: 44, name: 'Chittagong', country: 'Bangladesh' },
    'LKCMB': { x: 67, y: 52, name: 'Colombo', country: 'Sri Lanka' },
    'ITTSO': { x: 50, y: 34, name: 'Tesero', country: 'Italy' },
    'BRSSZ': { x: 33, y: 60, name: 'Santos', country: 'Brazil' },
    'ZADUR': { x: 55, y: 68, name: 'Durban', country: 'South Africa' },
    'AUMEL': { x: 87, y: 70, name: 'Melbourne', country: 'Australia' },
};

const extractPortCode = (portStr) => {
    if (!portStr) return null;
    const match = portStr.match(/\[([A-Z]{5})\]/);
    return match ? match[1] : null;
};

const getShipmentStatus = (shipment) => {
    const now = new Date();
    const etd = shipment.etsAtPol ? new Date(shipment.etsAtPol) : null;
    const eta = shipment.etaAtPod ? new Date(shipment.etaAtPod) : null;
    if (!etd || !eta) return { status: 'Pending', color: 'blue', dotColor: 'bg-blue-400' };
    if (now < etd) return { status: 'Pending', color: 'blue', dotColor: 'bg-blue-400' };
    if (now > eta) {
        const daysPast = Math.floor((now - eta) / (1000 * 60 * 60 * 24));
        if (daysPast > 2) return { status: 'Delayed', color: 'red', dotColor: 'bg-red-500' };
        return { status: 'Arrived', color: 'green', dotColor: 'bg-green-500' };
    }
    const totalDays = (eta - etd) / (1000 * 60 * 60 * 24);
    const elapsedDays = (now - etd) / (1000 * 60 * 60 * 24);
    const progress = (elapsedDays / totalDays) * 100;
    if (progress > 80) return { status: 'Arriving', color: 'amber', dotColor: 'bg-amber-400' };
    return { status: 'On Time', color: 'green', dotColor: 'bg-green-500' };
};

const getProgress = (s) => {
    const etd = s.etsAtPol ? new Date(s.etsAtPol) : null;
    const eta = s.etaAtPod ? new Date(s.etaAtPod) : null;
    if (!etd || !eta) return 0;
    const now = new Date();
    return Math.min(100, Math.max(0, ((now - etd) / (eta - etd)) * 100));
};

const getMilestoneStep = (s) => {
    const progress = getProgress(s);
    if (progress >= 100) return 8;
    if (progress >= 85) return 7;
    if (progress >= 60) return 6;
    if (progress >= 40) return 5;
    if (progress >= 20) return 4;
    if (progress >= 10) return 3;
    if (progress >= 5) return 2;
    if (progress > 0) return 1;
    return 0;
};

const OceanControlTower = () => {
    const [shipments, setShipments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState({});
    const [selectedShipment, setSelectedShipment] = useState(null);
    const [filter, setFilter] = useState('ALL');
    const [searchTerm, setSearchTerm] = useState('');
    const [aiRisks, setAiRisks] = useState([]);
    const [aiLoading, setAiLoading] = useState(false);
    const [showAIPanel, setShowAIPanel] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 8;
    
    // Terminal Tracking State
    const [terminalSearchTerm, setTerminalSearchTerm] = useState('');
    const [selectedContainer, setSelectedContainer] = useState(null);
    const [showTerminalTimeline, setShowTerminalTimeline] = useState(false);
    
    // Mock Terminal Tracking Data
    const terminalContainers = [
        { id: 'tc-001', containerNo: 'MSCU1234567', terminal: 'Rotterdam RWG', status: 'discharged', yardLocation: 'Yard A12', lastEvent: 'Discharged from vessel', timeAgo: '2 hrs ago', vesselName: 'MSC GULSUN', freeDays: 5, hazardous: false },
        { id: 'tc-002', containerNo: 'MSCU7894561', terminal: 'NY Maher', status: 'gate_out', yardLocation: 'Truck Lane', lastEvent: 'Picked up by truck', timeAgo: '30 min ago', vesselName: 'MAERSK EDEN', freeDays: 0, hazardous: false },
        { id: 'tc-003', containerNo: 'COSCO2468135', terminal: 'LA APMT', status: 'customs_hold', yardLocation: 'Yard C05', lastEvent: 'Customs inspection', timeAgo: '4 hrs ago', vesselName: 'COSCO HOPE', freeDays: 3, hazardous: true },
        { id: 'tc-004', containerNo: 'OOCL9876543', terminal: 'Rotterdam RWG', status: 'available', yardLocation: 'Yard B08', lastEvent: 'Released from customs', timeAgo: '1 hr ago', vesselName: 'OOCL OSAKA', freeDays: 4, hazardous: false },
        { id: 'tc-005', containerNo: 'HLCU5551234', terminal: 'Hamburg Eurogate', status: 'yard_storage', yardLocation: 'Yard D22', lastEvent: 'Moved to storage', timeAgo: '6 hrs ago', vesselName: 'HAPAG EXPRESS', freeDays: 7, hazardous: false },
        { id: 'tc-006', containerNo: 'MAEU1122334', terminal: 'NY Maher', status: 'gate_in', yardLocation: 'Gate 5', lastEvent: 'Container checked in', timeAgo: '45 min ago', vesselName: 'MAERSK BROOKLYN', freeDays: 10, hazardous: false },
        { id: 'tc-007', containerNo: 'TCLU8877665', terminal: 'LA APMT', status: 'loaded', yardLocation: 'Berth 3', lastEvent: 'Loaded on vessel', timeAgo: '3 hrs ago', vesselName: 'YANG MING UNION', freeDays: 0, hazardous: false },
        { id: 'tc-008', containerNo: 'ONEUCN12345', terminal: 'Singapore PSA', status: 'available', yardLocation: 'Yard E15', lastEvent: 'Available for pickup', timeAgo: '5 hrs ago', vesselName: 'ONE COSMOS', freeDays: 2, hazardous: false },
    ];
    
    // Terminal Performance Data
    const terminalPerformance = [
        { terminal: 'RWG Rotterdam', containers: 120, avgDwellTime: '2.4 days', congestion: 'low' },
        { terminal: 'NY Maher', containers: 95, avgDwellTime: '3.1 days', congestion: 'medium' },
        { terminal: 'LA APMT', containers: 210, avgDwellTime: '4.2 days', congestion: 'high' },
        { terminal: 'Hamburg Eurogate', containers: 78, avgDwellTime: '2.8 days', congestion: 'low' },
        { terminal: 'Singapore PSA', containers: 156, avgDwellTime: '1.9 days', congestion: 'low' },
    ];
    
    const getTerminalStatusColor = (status) => {
        switch(status) {
            case 'gate_in': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
            case 'yard_storage': return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
            case 'loaded': return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
            case 'discharged': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
            case 'customs_hold': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
            case 'available': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
            case 'gate_out': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            default: return 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400';
        }
    };
    
    const getTerminalStatusLabel = (status) => {
        switch(status) {
            case 'gate_in': return 'Gate In';
            case 'yard_storage': return 'Yard Storage';
            case 'loaded': return 'Loaded on Vessel';
            case 'discharged': return 'Discharged';
            case 'customs_hold': return 'Customs Hold';
            case 'available': return 'Available for Pickup';
            case 'gate_out': return 'Gate Out';
            default: return status;
        }
    };
    
    // Terminal Timeline Steps
    const terminalTimelineSteps = [
        { step: 'gate_in', label: 'Gate In Terminal', icon: ArrowDown },
        { step: 'customs_check', label: 'Customs Inspection', icon: ShieldAlert },
        { step: 'loaded', label: 'Loaded on Vessel', icon: Ship },
        { step: 'discharged', label: 'Discharged at Destination', icon: Boxes },
        { step: 'available', label: 'Available for Pickup', icon: CheckCircle },
        { step: 'gate_out', label: 'Gate Out', icon: Truck },
    ];
    
    const getContainerTimelineStep = (status) => {
        const stepOrder = ['gate_in', 'yard_storage', 'loaded', 'discharged', 'customs_hold', 'available', 'gate_out'];
        return Math.max(stepOrder.indexOf(status), 0);
    };

    const fetchShipments = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            console.log({ token })
            const res = await axios.get(`${BACKEND_URL}v1/Booking/GetBookingList?IPageNo=1&IPageRecords=50&FilterType=${filter}&SearchKeyword=${searchTerm}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.data?.statuscode === 200) {
                setShipments(res.data.data || []);
                setSummary(res.data.summary || {});
            }
        } catch (err) { console.error(err); }
        setLoading(false);
        setLastRefresh(new Date());
    }, [filter, searchTerm]);

    useEffect(() => { fetchShipments(); }, [fetchShipments]);

    const fetchAIRisks = async () => {
        setAiLoading(true); setShowAIPanel(true);
        try {
            const summaryText = shipments.slice(0, 10).map(s =>
                `ID:${s.fileNumber}, Route:${s.portOfLoading}→${s.portOfDischarge}, Carrier:${s.carrier}, Vessel:${s.vessel}, ETD:${s.etsAtPol}, ETA:${s.etaAtPod}`
            ).join('\n');
            const res = await axios.post(`${BACKEND_URL}/api/ai/risk-prediction`, { shipments: summaryText });
            setAiRisks(res.data?.risks || []);
        } catch (err) { console.error(err); }
        setAiLoading(false);
    };

    const metrics = {
        total: summary.total || shipments.length,
        delayed: shipments.filter(s => getShipmentStatus(s).status === 'Delayed').length,
        arriving: shipments.filter(s => getShipmentStatus(s).status === 'Arriving').length,
        onTime: shipments.filter(s => getShipmentStatus(s).status === 'On Time').length,
        pending: shipments.filter(s => getShipmentStatus(s).status === 'Pending').length,
        exceptions: summary.needattension || 0,
    };
    const onTimeRate = metrics.total > 0 ? Math.round(((metrics.total - metrics.delayed) / metrics.total) * 100) : 0;

    const routes = shipments.map(s => ({
        id: s.id, origin: extractPortCode(s.portOfLoading), dest: extractPortCode(s.portOfDischarge),
        status: getShipmentStatus(s), vessel: s.vessel, carrier: s.carrier, fileNumber: s.fileNumber,
    })).filter(r => r.origin && r.dest);

    const portActivity = {};
    shipments.forEach(s => {
        const pol = extractPortCode(s.portOfLoading);
        const pod = extractPortCode(s.portOfDischarge);
        if (pol) portActivity[pol] = (portActivity[pol] || 0) + 1;
        if (pod) portActivity[pod] = (portActivity[pod] || 0) + 1;
    });

    const carrierStats = {};
    shipments.forEach(s => {
        const c = s.carrier || 'Unknown';
        if (!carrierStats[c]) carrierStats[c] = { total: 0, onTime: 0, delayed: 0 };
        carrierStats[c].total++;
        if (getShipmentStatus(s).status === 'Delayed') carrierStats[c].delayed++;
        else carrierStats[c].onTime++;
    });

    // Pagination
    const totalPages = Math.ceil(shipments.length / pageSize);
    const paginatedShipments = shipments.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const milestoneLabels = ['Booked', 'Docs Ready', 'Gate In', 'Loaded', 'Departed', 'In Transit', 'Arrived', 'Discharged', 'Delivered'];

    return (
        <div className="p-4 space-y-3 min-h-screen">
            {/* Header */}
            <div data-testid="ocean-control-tower-header" className="bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 rounded-xl p-4 relative overflow-hidden">
                <div className="absolute inset-0 opacity-15">
                    <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-500 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />
                </div>
                <div className="relative">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-cyan-500/20 rounded-xl border border-cyan-500/30">
                                <Globe className="w-6 h-6 text-cyan-400" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-white">Ocean Control Tower</h1>
                                <p className="text-xs text-blue-300/80">Real-time visibility across all ocean operations</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 border border-emerald-500/30 rounded-lg">
                                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                                <span className="text-xs font-medium text-emerald-400">Live</span>
                            </div>
                            <button onClick={fetchShipments} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs text-blue-300 flex items-center gap-1.5 transition-all border border-white/10">
                                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                            </button>
                            <button onClick={fetchAIRisks} className="px-3 py-1.5 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 shadow-lg">
                                <Sparkles className="w-3.5 h-3.5" /> AI Risk Analysis
                            </button>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    <div data-testid="kpi-cards-container" className="grid grid-cols-5 md:grid-cols-10 gap-2">
                        {[
                            { label: 'ACTIVE VESSELS', value: metrics.total, icon: Ship, accent: 'text-blue-400 border-blue-500/30' },
                            { label: 'ON-TIME RATE', value: `${onTimeRate}%`, icon: CheckCircle, accent: 'text-emerald-400 border-emerald-500/30' },
                            { label: 'DELAYED', value: metrics.delayed, icon: AlertTriangle, accent: 'text-red-400 border-red-500/30' },
                            { label: 'IN TRANSIT', value: metrics.onTime, icon: Container, accent: 'text-cyan-400 border-cyan-500/30' },
                            { label: 'AVG TRANSIT', value: '28.5 days', icon: Clock, accent: 'text-purple-400 border-purple-500/30' },
                            { label: 'CARGO VALUE', value: '$2.4M', icon: DollarSign, accent: 'text-amber-400 border-amber-500/30' },
                            { label: 'CRITICAL ALERTS', value: metrics.exceptions, icon: ShieldAlert, accent: metrics.exceptions > 0 ? 'text-red-400 border-red-500/50 bg-red-500/10' : 'text-slate-400 border-slate-600' },
                            { label: 'ARRIVING SOON', value: metrics.arriving, icon: Navigation, accent: 'text-amber-400 border-amber-500/30' },
                            { label: 'AT TERMINAL', value: terminalContainers.length, icon: Warehouse, accent: 'text-indigo-400 border-indigo-500/30' },
                            { label: 'READY PICKUP', value: terminalContainers.filter(c => c.status === 'available').length, icon: BoxSelect, accent: 'text-emerald-400 border-emerald-500/30' },
                        ].map((kpi, i) => (
                            <div key={i} className={`rounded-lg p-2.5 border bg-white/5 ${kpi.accent}`}>
                                <kpi.icon className={`w-4 h-4 mb-1.5 ${kpi.accent.split(' ')[0]}`} />
                                <p className="text-lg font-bold text-white">{kpi.value}</p>
                                <p className="text-[9px] uppercase tracking-wider text-slate-400 mt-0.5">{kpi.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* AI Risk Panel */}
            <AnimatePresence>
                {showAIPanel && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                        <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-lg border border-violet-200 dark:border-violet-800 p-3">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-600" /><h3 className="text-sm font-semibold text-violet-800 dark:text-violet-300">AI Risk Predictions</h3></div>
                                <button onClick={() => setShowAIPanel(false)} className="p-1 hover:bg-violet-200 dark:hover:bg-violet-800 rounded-md"><X className="w-3.5 h-3.5 text-violet-500" /></button>
                            </div>
                            {aiLoading ? (
                                <div className="flex items-center gap-2 text-xs text-violet-600 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Analyzing shipment data...</div>
                            ) : aiRisks.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {aiRisks.map((risk, i) => (
                                        <div key={i} className={`p-2 rounded-md border text-xs ${risk.risk === 'high' ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' : risk.risk === 'medium' ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800' : 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'}`}>
                                            <div className="flex items-center gap-1 mb-1">
                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${risk.risk === 'high' ? 'bg-red-500 text-white' : risk.risk === 'medium' ? 'bg-amber-500 text-white' : 'bg-green-500 text-white'}`}>{risk.risk}</span>
                                                <span className="font-semibold text-slate-700 dark:text-slate-300">{risk.shipmentId}</span>
                                            </div>
                                            <p className="text-slate-600 dark:text-slate-400">{risk.reason}</p>
                                            {risk.recommendation && <p className="text-violet-600 dark:text-violet-400 mt-1 italic">{risk.recommendation}</p>}
                                        </div>
                                    ))}
                                </div>
                            ) : <p className="text-xs text-violet-600 py-2">Click "AI Risk Analysis" to generate predictions.</p>}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Live Vessel Tracking Map + Exceptions & Alerts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {/* Map - 2/3 width */}
                <div className="lg:col-span-2">
                    <LiveVesselMap height="450px" showTitle={true} />
                </div>

                {/* Exceptions & Alerts */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                    <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-500" /> Exceptions & Alerts
                        </h2>
                        <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-[10px] font-bold rounded-full">{metrics.delayed + metrics.exceptions} open</span>
                    </div>
                    <div className="p-3 space-y-2 max-h-[380px] overflow-y-auto">
                        {shipments.filter(s => ['Delayed', 'Pending'].includes(getShipmentStatus(s).status)).slice(0, 8).map((s, i) => {
                            const st = getShipmentStatus(s);
                            const alertTypes = [
                                { type: 'Cargo Rollover', icon: RotateCcw, desc: 'Cargo rolled to next sailing' },
                                { type: 'Vessel Delay', icon: Clock, desc: `Vessel ${s.vessel || 'unknown'} delayed` },
                                { type: 'Customs Hold', icon: ShieldAlert, desc: 'Container held at customs' },
                                { type: 'Documentation', icon: FileWarning, desc: 'Missing documentation' },
                                { type: 'Schedule Change', icon: Calendar, desc: 'Schedule updated by carrier' },
                            ];
                            const alert = alertTypes[i % alertTypes.length];
                            return (
                                <div key={i} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-100 dark:border-slate-700/50">
                                    <div className="flex items-start justify-between mb-1.5">
                                        <div className="flex items-center gap-2">
                                            <div className={`p-1.5 rounded-md ${st.status === 'Delayed' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                                                <alert.icon className={`w-3.5 h-3.5 ${st.status === 'Delayed' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
                                            </div>
                                            <span className="text-xs font-bold text-slate-800 dark:text-white">{alert.type}</span>
                                        </div>
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${st.status === 'Delayed' ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'}`}>HIGH</span>
                                    </div>
                                    <p className="text-[11px] text-slate-600 dark:text-slate-400 mb-1.5">{alert.desc}</p>
                                    <div className="flex items-center gap-1 text-[10px] text-slate-400">
                                        <span className="font-medium">{s.carrier}</span>
                                        <span>•</span>
                                        <span>{s.fileNumber}</span>
                                        <span>•</span>
                                        <span>2d ago</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Active Shipments */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                        <Anchor className="w-4 h-4 text-blue-500" /> Active Shipments
                    </h2>
                    <div className="flex items-center gap-2">
                        <div className="flex bg-slate-100 dark:bg-slate-800 rounded-md p-0.5">
                            {['ALL', 'Active', 'Delayed'].map(f => (
                                <button key={f} onClick={() => { setFilter(f); setCurrentPage(1); }} className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${filter === f ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-700'}`}>{f}</button>
                            ))}
                        </div>
                        <div className="relative">
                            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="pl-7 pr-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 w-32 focus:ring-1 focus:ring-blue-500" />
                        </div>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead><tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                            {['Vessel', 'Route', 'ETA', 'Progress', 'Status', 'TEU'].map(h => (
                                <th key={h} className="px-3 py-2 text-left font-semibold text-slate-500 dark:text-slate-400">{h}</th>
                            ))}
                        </tr></thead>
                        <tbody>
                            {loading ? <tr><td colSpan={6} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-500 mx-auto" /></td></tr> :
                                paginatedShipments.map((s, i) => {
                                    const st = getShipmentStatus(s);
                                    const eta = s.etaAtPod ? new Date(s.etaAtPod) : null;
                                    const progress = Math.round(getProgress(s));
                                    return (
                                        <tr key={i} className="border-b border-slate-100 dark:border-slate-800 hover:bg-blue-50/50 dark:hover:bg-slate-800/30">
                                            <td className="px-3 py-2.5">
                                                <p className="font-semibold text-slate-800 dark:text-white">{s.vessel || 'TBA'}</p>
                                                <p className="text-[10px] text-slate-400">{s.carrier} • {s.voyage || s.fileNumber}</p>
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <div className="flex items-center gap-1 text-slate-700 dark:text-slate-300">
                                                    <span className="truncate max-w-[80px]">{s.portOfLoading?.split('[')[0]?.split(',')[0]?.trim()}</span>
                                                    <ArrowRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                                    <span className="truncate max-w-[80px]">{s.portOfDischarge?.split('[')[0]?.split(',')[0]?.trim()}</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2.5 text-slate-600 dark:text-slate-400">{eta ? eta.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) : '-'}</td>
                                            <td className="px-3 py-2.5">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-16 bg-slate-100 dark:bg-slate-800 rounded-full h-1.5">
                                                        <div className={`h-full rounded-full ${st.color === 'red' ? 'bg-red-500' : st.color === 'amber' ? 'bg-amber-400' : 'bg-emerald-500'}`} style={{ width: `${progress}%` }} />
                                                    </div>
                                                    <span className="text-[10px] text-slate-400 w-8">{progress}%</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2.5">
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${st.color === 'green' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                    st.color === 'red' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                                        st.color === 'amber' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                                            'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                                    }`}>{st.status}</span>
                                            </td>
                                            <td className="px-3 py-2.5 font-medium text-slate-700 dark:text-slate-300">{Math.floor(Math.random() * 3000 + 500).toLocaleString()}</td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
                {/* Pagination */}
                <div className="px-4 py-2.5 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                    <span className="text-[10px] text-slate-400">Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, shipments.length)} of {shipments.length}</span>
                    <div className="flex items-center gap-1">
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30"><ChevronLeft className="w-4 h-4 text-slate-500" /></button>
                        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(p => (
                            <button key={p} onClick={() => setCurrentPage(p)} className={`w-6 h-6 rounded text-[10px] font-medium ${currentPage === p ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}>{p}</button>
                        ))}
                        {totalPages > 5 && <span className="text-slate-400 text-xs">...</span>}
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30"><ChevronRight className="w-4 h-4 text-slate-500" /></button>
                    </div>
                </div>
            </div>

            {/* Terminal Tracking Section */}
            <div data-testid="terminal-tracking-section" className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                        <Warehouse className="w-4 h-4 text-indigo-500" /> Terminal Tracking
                    </h2>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input 
                                data-testid="terminal-search-input"
                                type="text" 
                                placeholder="Search container / BL..." 
                                value={terminalSearchTerm} 
                                onChange={(e) => setTerminalSearchTerm(e.target.value)} 
                                className="pl-7 pr-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 w-48 focus:ring-1 focus:ring-indigo-500" 
                            />
                        </div>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table data-testid="terminal-tracking-table" className="w-full text-xs">
                        <thead>
                            <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                                {['Container', 'Terminal', 'Status', 'Yard Location', 'Last Event', 'Time', 'Free Days', 'Action'].map(h => (
                                    <th key={h} className="px-3 py-2 text-left font-semibold text-slate-500 dark:text-slate-400">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {terminalContainers
                                .filter(c => !terminalSearchTerm || 
                                    c.containerNo.toLowerCase().includes(terminalSearchTerm.toLowerCase()) ||
                                    c.terminal.toLowerCase().includes(terminalSearchTerm.toLowerCase())
                                )
                                .map((container, i) => (
                                <tr key={i} className="border-b border-slate-100 dark:border-slate-800 hover:bg-indigo-50/50 dark:hover:bg-slate-800/30" data-testid={`terminal-container-row-${container.containerNo}`}>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center gap-2">
                                            <p className="font-semibold text-slate-800 dark:text-white">{container.containerNo}</p>
                                            {container.hazardous && (
                                                <span className="px-1 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-[9px] font-bold rounded">HAZ</span>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-slate-400">{container.vesselName}</p>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center gap-1.5">
                                            <Building2 className="w-3 h-3 text-slate-400" />
                                            <span className="text-slate-700 dark:text-slate-300">{container.terminal}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span data-testid={`status-badge-${container.containerNo}`} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${getTerminalStatusColor(container.status)}`}>
                                            {getTerminalStatusLabel(container.status)}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5 text-slate-600 dark:text-slate-400">{container.yardLocation}</td>
                                    <td className="px-3 py-2.5 text-slate-600 dark:text-slate-400">{container.lastEvent}</td>
                                    <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400">{container.timeAgo}</td>
                                    <td className="px-3 py-2.5">
                                        <span className={`font-medium ${container.freeDays <= 2 ? 'text-red-600 dark:text-red-400' : container.freeDays <= 5 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-600 dark:text-slate-400'}`}>
                                            {container.freeDays} days
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <button 
                                            data-testid={`timeline-btn-${container.containerNo}`}
                                            onClick={() => { setSelectedContainer(container); setShowTerminalTimeline(true); }}
                                            className="px-2 py-1 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-[10px] font-medium hover:bg-indigo-200 dark:hover:bg-indigo-900/50 flex items-center gap-1"
                                        >
                                            <Eye className="w-3 h-3" /> Timeline
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Carrier Performance + Terminal Performance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Carrier Performance */}
                <div data-testid="carrier-performance-widget" className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                    <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-800">
                        <h2 className="text-sm font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-blue-500" /> Carrier Performance
                        </h2>
                    </div>
                    <div className="p-3 space-y-3">
                        {Object.entries(carrierStats).sort((a, b) => b[1].total - a[1].total).slice(0, 6).map(([carrier, stats], i) => {
                            const reliability = stats.total > 0 ? ((stats.onTime / stats.total) * 100).toFixed(1) : 0;
                            const colors = ['bg-amber-500', 'bg-blue-600', 'bg-slate-700', 'bg-rose-500', 'bg-red-600', 'bg-emerald-500'];
                            const initials = carrier.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                            return (
                                <div key={i} className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-lg ${colors[i % colors.length]} flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0`}>{initials}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs font-semibold text-slate-800 dark:text-white truncate">{carrier}</span>
                                            <span className={`text-xs font-bold ${parseFloat(reliability) >= 80 ? 'text-emerald-500' : parseFloat(reliability) >= 60 ? 'text-amber-500' : 'text-red-500'}`}>{reliability}%</span>
                                        </div>
                                        <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1.5">
                                            <div className={`h-full rounded-full transition-all ${parseFloat(reliability) >= 80 ? 'bg-emerald-500' : parseFloat(reliability) >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${reliability}%` }} />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Terminal Performance */}
                <div data-testid="terminal-performance-widget" className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                    <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-800">
                        <h2 className="text-sm font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                            <Warehouse className="w-4 h-4 text-indigo-500" /> Terminal Performance
                        </h2>
                    </div>
                    <div className="p-3 space-y-3">
                        {terminalPerformance.map((terminal, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50 dark:bg-slate-800/30">
                                <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                                    <Building2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-semibold text-slate-800 dark:text-white truncate">{terminal.terminal}</span>
                                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                                            terminal.congestion === 'low' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                                            terminal.congestion === 'medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                        }`}>{terminal.congestion}</span>
                                    </div>
                                    <div className="flex items-center gap-4 text-[10px] text-slate-500 dark:text-slate-400">
                                        <span className="flex items-center gap-1">
                                            <Container className="w-3 h-3" /> {terminal.containers} containers
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Timer className="w-3 h-3" /> {terminal.avgDwellTime} avg dwell
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-800">
                    <h2 className="text-sm font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                        <Zap className="w-4 h-4 text-emerald-500" /> Shipment Milestones
                    </h2>
                </div>
                <div className="p-3 space-y-4 max-h-[400px] overflow-y-auto">
                    {shipments.slice(0, 5).map((s, idx) => {
                        const st = getShipmentStatus(s);
                        const step = getMilestoneStep(s);
                        return (
                            <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-800/30 rounded-lg border border-slate-100 dark:border-slate-700/50">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <p className="text-xs font-bold text-slate-800 dark:text-white">{s.fileNumber}</p>
                                        <p className="text-[10px] text-slate-400">{s.vessel || 'TBA'} • {s.portOfLoading?.split('[')[0]?.split(',')[0]?.trim()} → {s.portOfDischarge?.split('[')[0]?.split(',')[0]?.trim()}</p>
                                    </div>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${st.color === 'green' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                        st.color === 'red' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                            st.color === 'amber' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                                'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                        }`}>{st.status}</span>
                                </div>
                                <div className="flex items-center gap-0">
                                    {milestoneLabels.map((label, mi) => (
                                        <React.Fragment key={mi}>
                                            <div className="flex flex-col items-center flex-shrink-0">
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold border-2 ${mi < step ? 'bg-emerald-500 border-emerald-500 text-white' :
                                                    mi === step ? 'bg-amber-400 border-amber-400 text-white animate-pulse' :
                                                        'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-400'
                                                    }`}>
                                                    {mi < step ? <CheckCircle className="w-3 h-3" /> : mi + 1}
                                                </div>
                                                <span className={`text-[8px] mt-1 whitespace-nowrap ${mi <= step ? 'text-emerald-600 dark:text-emerald-400 font-medium' : 'text-slate-400'}`}>{label}</span>
                                            </div>
                                            {mi < milestoneLabels.length - 1 && (
                                                <div className={`flex-1 h-0.5 mx-0.5 mt-[-12px] ${mi < step ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'}`} />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Terminal Timeline Modal */}
            <AnimatePresence>
                {showTerminalTimeline && selectedContainer && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setShowTerminalTimeline(false)}
                    >
                        <motion.div
                            data-testid="terminal-timeline-modal"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white/10 rounded-lg">
                                        <Container className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold">Container Timeline</h2>
                                        <p className="text-sm text-white/80">{selectedContainer.containerNo}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowTerminalTimeline(false)}
                                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="p-6">
                                {/* Container Info */}
                                <div className="grid grid-cols-2 gap-3 mb-6">
                                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Terminal</p>
                                        <p className="text-sm font-semibold text-slate-800 dark:text-white">{selectedContainer.terminal}</p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Status</p>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getTerminalStatusColor(selectedContainer.status)}`}>
                                            {getTerminalStatusLabel(selectedContainer.status)}
                                        </span>
                                    </div>
                                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Yard Location</p>
                                        <p className="text-sm font-semibold text-slate-800 dark:text-white">{selectedContainer.yardLocation}</p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Free Days Remaining</p>
                                        <p className={`text-sm font-semibold ${selectedContainer.freeDays <= 2 ? 'text-red-600' : 'text-slate-800 dark:text-white'}`}>
                                            {selectedContainer.freeDays} days
                                        </p>
                                    </div>
                                </div>

                                {/* AI Risk Alert */}
                                {selectedContainer.status === 'customs_hold' && (
                                    <div className="mb-6 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50">
                                        <div className="flex items-center gap-2 mb-1">
                                            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                            <span className="text-xs font-bold text-amber-700 dark:text-amber-400">AI Risk Alert</span>
                                        </div>
                                        <p className="text-xs text-amber-800 dark:text-amber-300">
                                            Container held at customs. Expected delay: 1-2 days. Consider notifying customer.
                                        </p>
                                    </div>
                                )}

                                {/* Timeline */}
                                <h3 className="text-sm font-semibold text-slate-800 dark:text-white mb-4">Terminal Activity Timeline</h3>
                                <div className="relative pl-6 space-y-4">
                                    <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700" />
                                    {terminalTimelineSteps.map((step, idx) => {
                                        const currentStep = getContainerTimelineStep(selectedContainer.status);
                                        const isCompleted = idx <= currentStep;
                                        const isCurrent = idx === currentStep;
                                        return (
                                            <div key={idx} className="relative flex items-start gap-3">
                                                <div className={`absolute left-[-16px] w-4 h-4 rounded-full border-2 ${
                                                    isCompleted ? 'bg-emerald-500 border-emerald-500' : 
                                                    'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600'
                                                } ${isCurrent ? 'ring-2 ring-emerald-300 ring-offset-2 dark:ring-offset-slate-900' : ''}`}>
                                                    {isCompleted && <CheckCircle className="w-2.5 h-2.5 text-white absolute top-0.5 left-0.5" />}
                                                </div>
                                                <div className={`flex-1 p-3 rounded-lg ${
                                                    isCurrent ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/50' : 
                                                    'bg-slate-50 dark:bg-slate-800/30'
                                                }`}>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <step.icon className={`w-4 h-4 ${isCompleted ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400'}`} />
                                                        <span className={`text-xs font-semibold ${isCompleted ? 'text-slate-800 dark:text-white' : 'text-slate-400'}`}>
                                                            {step.label}
                                                        </span>
                                                    </div>
                                                    {isCurrent && (
                                                        <p className="text-[10px] text-emerald-600 dark:text-emerald-400">
                                                            Current status • {selectedContainer.timeAgo}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default OceanControlTower;